<template>
	<client-only>
		<AdaptiveContainer v-if="loading">
			<div class="block w-[1000vw] mt-6 mb-12">
				<div v-for="index in 5" :key="index" class="inline-block float-left w-[320px] mr-10">
					<DjCardCompany :loading="true" newTab />
				</div>
			</div>
		</AdaptiveContainer>
		<carousel
			ref="carouselEl"
			v-if="showCarousel && !loading"
			:settings="settings"
			class="pb-5"
			:class="`carousel-dj carousel-companies carousel-buttons-${buttonType}`"
		>
			<slide
				v-for="(item, index) in carouselData.concat(new Array(helperSlides))"
				:key="index"
				:style="{ paddingRight: slideMargin + 'px' }"
			>
				<DjCardCompanyBanner
					v-if="index == 0 && !isMobileValue && showBanner == true"
					:subtitle="banner.sub_title[locale]"
					:title="banner.title[locale]"
					:description="banner.content[locale]"
					:cta="banner.cta_text[locale]"
					:url="banner.cta_link[locale]"
					:logo="banner.image.cloudinary"
				/>
				<CardCompany
					v-else-if="
						index < carouselData.length + (index == 0 && !isMobileValue && showBanner == true ? 1 : 0)
					"
					:key="index"
					:company="item"
					:langs="langs"
					:locale="locale"
					newTab
				/>
				<div v-else></div>
			</slide>

			<template v-if="showCarouselAddons()" #addons="{ currentSlide, slidesCount }">
				<!-- Custom navigation -->
				<div class="carousel__navigation-buttons mx-auto">
					<button
						@click="slidePrev(currentSlide, containerSlideNumber)"
						class="carousel__prev transition-colors"
						:class="{ 'hide-prev': Math.ceil(currentSlide) < 1 }"
					></button>
					<button
						@click="
							slideTo(
								currentSlide + containerSlideNumber,
								slidesCount - helperSlides - containerSlideNumber
							)
						"
						class="carousel__next transition-colors"
						:class="{
							'hide-next': Math.ceil(currentSlide) >= slidesCount - containerSlideNumber - helperSlides,
						}"
					></button>
				</div>

				<!-- Custom pagination -->
				<div class="hidden sm:block carousel__custom-pagination mx-auto">
					<button
						v-for="(paginationEl, index) in Math.max(
							1,
							Math.ceil((slidesCount - helperSlides) / containerSlideNumber)
						)"
						:key="index"
						@click="
							slideTo(index * containerSlideNumber, slidesCount - helperSlides - containerSlideNumber)
						"
						class="h-4 w-4 m-2 bg-dj-gray-light rounded-full"
						:class="{
							'dot--active': activePagination(
								index,
								currentSlide,
								slidesCount,
								containerSlideNumber,
								helperSlides
							),
						}"
					></button>
				</div>
			</template>
		</carousel>
		<template #fallback>
			<AdaptiveContainer v-if="!loading && ssrStaticCompanies.length > 0">
				<div class="block w-[1000vw] mt-6 mb-12">
					<div
						v-for="(item, index) in ssrStaticCompanies"
						:key="item.id || index"
						class="inline-block float-left w-[320px] mr-10"
					>
						<CardCompany :company="item" :langs="langs" :locale="locale" newTab />
					</div>
				</div>
			</AdaptiveContainer>
			<AdaptiveContainer v-else-if="fetchError">
				<div class="block w-[1000vw] mt-6 mb-12">
					<div v-for="index in 5" :key="index" class="inline-block float-left w-[320px] mr-10">
						<DjCardCompany :loading="true" newTab />
					</div>
				</div>
			</AdaptiveContainer>
		</template>
	</client-only>
</template>

<script lang="ts">
import { ref, defineComponent } from "vue";
// import "vue3-carousel/dist/carousel.css";
import { Carousel, Slide, Pagination, Navigation } from "vue3-carousel";
import { useI18n } from "vue-i18n";

import { getDjContainerWidth } from "../../composables/carouselHelpers";

type CompanyItem = {
	id?: number;
	[key: string]: unknown;
};

export default defineComponent({
	name: "CarouselCompanies",
	components: {
		Carousel,
		Slide,
		Pagination,
		Navigation,
	},
	props: {
		slideWidth: {
			type: Number,
			default: 320,
		},
		buttonType: {
			type: String,
			default: "white",
			validator: (value: string) => {
				// valid values
				return ["white", "gray"].includes(value);
			},
		},
		showBanner: {
			type: Boolean,
			default: false,
		},
		rawCompaniesData: {
			type: Array,
			default: () => [],
		},
		banner: {
			type: Object,
			default: () => {
				return {};
			},
		},
	},
	emits: ["loaded"],
	async setup(props, { emit }) {
		const { locale } = useI18n();
		const langs = useLangs.job;
		const regionCode = useRegion.getCurrentRegion().code.toLowerCase() || "hu";
		const companiesCacheKey = `carousel-companies-${regionCode}-${globalConfig.isBlue ? "blue" : "default"}-${
			globalConfig.isNotBlueHU ? "notblue" : "all"
		}`;
		const carouselEl = ref<{ slideTo: (index: number) => void } | null>(null);
		const companyData = ref<CompanyItem[]>([]);
		const companiesCache = useState<CompanyItem[]>(companiesCacheKey, () => []);
		const loading = ref(true);
		const fetchError = ref(false);
		const showCarousel = ref(false);
		let bodyWidth = getBodyWidth();
		const slideMargin = ref(20);
		const helperSlides = ref(0);
		const containerSlideNumber = ref(0);
		const mainRequestUrl = ref("");

		const {
			data: mainResponse,
			error: mainError,
			execute: executeMainRequest,
		} = useFetch<{ data: CompanyItem[] }>(() => mainRequestUrl.value, {
			server: true,
			immediate: false,
			default: () => ({ data: [] }),
		});

		function getBodyWidth() {
			if (typeof document === "undefined") return 1280;
			return document.body?.offsetWidth || 1280;
		}

		const isMobileValue = ref(getBodyWidth() < 741);

		const isMobile = () => {
			return isMobileValue.value;
		};

		const carouselData = computed(() => {
			const array: CompanyItem[] = [];

			if (props.rawCompaniesData.length != 0) {
				array.push(...(props.rawCompaniesData as CompanyItem[]));
			} else if (companyData.value.length != 0) {
				array.push(...companyData.value);
			}

			if (!isMobileValue.value && props.showBanner && Object.keys(props.banner).length > 0 && array.length > 0) {
				array.unshift({});
			}

			return array;
		});

		const ssrStaticCompanies = computed(() => {
			if (props.rawCompaniesData.length != 0) {
				return [...(props.rawCompaniesData as CompanyItem[])].slice(0, 8);
			}

			return [...companyData.value].slice(0, 8);
		});

		function getRegionCode() {
			return regionCode;
		}

		// Calculate slide numbers from elements width
		function getItemsNumber() {
			return bodyWidth / (getValidSlideWidth() + slideMargin.value);
		}

		function showCarouselAddons() {
			const containerWidth = getDjContainerWidth();

			return (
				(bodyWidth - (bodyWidth - containerWidth) / 2) / (getValidSlideWidth() + slideMargin.value) <
				carouselData.value.length
			);
		}

		function slideTo(i: number, maxIndex: number) {
			carouselEl.value?.slideTo(i <= maxIndex ? i : maxIndex);
		}

		function slidePrev(currentSlide: number, containerSlideNumber: number) {
			let goToIndex =
				currentSlide -
				(currentSlide % containerSlideNumber == 0 ? containerSlideNumber : currentSlide % containerSlideNumber);
			carouselEl.value?.slideTo(goToIndex);
		}

		function activePagination(
			index: number,
			currentSlide: number,
			slidesCount: number,
			containerSlideNumber: number,
			helperSlides: number
		) {
			const lastPagination = index == Math.ceil((slidesCount - helperSlides) / containerSlideNumber) - 1;
			if (lastPagination && Math.ceil(currentSlide) >= slidesCount - containerSlideNumber - helperSlides) {
				return true;
			} else if (
				Math.floor(currentSlide / containerSlideNumber) == index &&
				Math.ceil(currentSlide) < slidesCount - containerSlideNumber - helperSlides
			) {
				return true;
			}

			return false;
		}

		// Carousel settings
		let settings = {
			itemsToShow: 1,
			snapAlign: "start",
			mouseDrag: true,
			touchDrag: true,
			transition: 800,
		};

		function getValidSlideWidth() {
			const contWidth = getDjContainerWidth();
			return contWidth > props.slideWidth ? props.slideWidth : contWidth;
		}

		function calculateCarouselParams() {
			const containerWidth = getDjContainerWidth();
			containerSlideNumber.value = Math.floor(containerWidth / getValidSlideWidth());
			bodyWidth = getBodyWidth();
			slideMargin.value =
				containerSlideNumber.value > 1
					? (containerWidth - containerSlideNumber.value * getValidSlideWidth()) /
					  (containerSlideNumber.value - 1)
					: 20;
			settings.itemsToShow = getItemsNumber();
			helperSlides.value =
				Math.ceil((bodyWidth - containerWidth) / 2 / (getValidSlideWidth() + slideMargin.value)) +
				(bodyWidth > 1800 ? 1 : 0);
		}

		function isResized() {
			showCarousel.value = false;
			setTimeout(() => {
				isMobileValue.value = getBodyWidth() < 741;
				// Recalculate carousel params
				calculateCarouselParams();
				showCarousel.value = true;
			}, 500);
		}

		let doResize: ReturnType<typeof setTimeout> | undefined = undefined;
		function handleResize() {
			if (doResize) clearTimeout(doResize);
			// Resize ONLY width change (mobile hiding navbar handling)
			bodyWidth != getBodyWidth() ? (doResize = setTimeout(isResized, 500)) : null;
		}

		async function getCompanies() {
			if (companiesCache.value.length > 0) {
				companyData.value = [...companiesCache.value];
				loading.value = false;
				showCarousel.value = true;
				emit("loaded");
				return;
			}

			const url = new URL(`${globalConfig.API}/companies`);

			url.searchParams.append("region", getRegionCode());
			url.searchParams.append("limit", "19");
			url.searchParams.append("scope[]", "highlightedGold");

			if (globalConfig.isBlue) {
				url.searchParams.append("scope[]", "isBlue");
			}

			if (globalConfig.isNotBlueHU) {
				url.searchParams.append("scope[]", "isNotBlue");
			}

			mainRequestUrl.value = `${url}`;
			await executeMainRequest();
			companyData.value = mainResponse.value?.data || [];
			companiesCache.value = [...companyData.value];

			loading.value = false;
			emit("loaded");

			if (mainError.value) {
				fetchError.value = true;
				showCarousel.value = false;
			}

			if (companyData.value.length == 0) {
				showCarousel.value = false;
			} else {
				showCarousel.value = true;
			}
		}

		onMounted(() => {
			isMobileValue.value = getBodyWidth() < 741;
			calculateCarouselParams();
			showCarousel.value = companyData.value.length > 0;

			window.addEventListener("resize", handleResize);
		});

		if (props.rawCompaniesData.length > 0) {
			companyData.value = [...(props.rawCompaniesData as CompanyItem[])];
			loading.value = false;
			emit("loaded");
		} else {
			await getCompanies();
		}

		return {
			loading,
			carouselEl,
			settings,
			slideMargin,
			showCarousel,
			helperSlides,
			companyData,
			carouselData,
			isMobile,
			isMobileValue,
			showCarouselAddons,
			containerSlideNumber,
			slidePrev,
			slideTo,
			activePagination,
			langs,
			locale,
			fetchError,
			ssrStaticCompanies,
		};
	},
});
</script>
<!-- <style> not working on prod -->
<!--
<style lang="scss">
@import '~/assets/css/carousel.scss';
body {
	// Body height fix for carousel initilization
	min-height: calc(100vh + 1px);
}

.carousel-companies {
	margin-top: -100px;
	
	ol.carousel__track {
		margin-top: 120px;
	}

	.carousel {

		&__slide {
			align-items: flex-start;
			justify-content: flex-start;
			text-align: left;
			& > div {
				width: 100%;
				height: 100%;
			}
		}
	}
}
</style>
-->
